import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { getAccountCocInSchedule } from '@app/reducers/account/account-service.selectors';
import { select, Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { anyDepartmentHasPaidBreaks } from '@reducers/orm/department/department.service';
import { usePermission } from '@sb/shared/permission/data-access';
import type { PermissionOption } from '@sb/shared/permission/util';
import { generateId } from '@sb/shared/util';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import {
  addPlainDays,
  fromPlainDate,
  plainDateToday,
  toISODateString,
  type PlainDateRange,
} from '@shiftbase-com/ts-shared/utilities';
import { BehaviorSubject, Subscription } from 'rxjs';
import { combineLatest, filter, startWith, switchMap, tap } from 'rxjs/operators';

import { CustomFieldsAppliesTo, CustomFieldsModel } from '../../../../reducers/orm/custom-fields/custom-fields.model';
import { getCustomFieldsByAppliesTo } from '../../../../reducers/orm/custom-fields/custom-fields.selectors';
import { TimesheetService } from '../../../../reducers/orm/timesheet/timesheet.service';
import { TimesheetColumns } from '../../../../reducers/page-filters/page-filters.model';
import { isAnyTimesheetFilterActive, PageFiltersService } from '../../../../reducers/page-filters/page-filters.service';
import { lazySelect } from '../../../../shared/lazy-select.observable';
import { TimesheetDayService } from '../../../+timesheet/shared/day/timesheet-day-service';
import { type TimesheetRow } from '../../../+timesheet/shared/interfaces';
import { TimesheetHelperService } from '../../../+timesheet/shared/timesheet-helper.service';
import { isTimesheetRowAbsentee, isTimesheetRowTimesheet } from '../../../+timesheet/shared/timesheet-row.helper';
import { SidebarComponent } from '../../../shared/sidebar/sidebar.component';
import {
  getTimesheetDepartmentsForUsers,
  getTimesheetPageOptionsForAllDepartments,
} from './../../../+timesheet/shared/timesheet-helper.service';
import { getDataForEmployeeTimesheetPage } from './employee-hours.helper';
import { EmployeeTimesheetData, EmployeeTimesheetDays } from './interfaces';

@Component({
  standalone: false,
  selector: 'employee-hours-overview',
  templateUrl: './employee-hours-overview.component.html',
  styleUrls: ['./employee-hours-overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // The grid's scroll container sizes itself against the page, and takes that
  // height from here: a cap rather than a height, so a short period still ends at
  // its totals row instead of stretching to the window. Its minimum height is the
  // floor in the component stylesheet, which is why no min-h-* utility sits here:
  // they are emitted !important and would override it.
  host: {
    class: 'flex max-h-full flex-col',
  },
  providers: [TimesheetDayService],
})
export class EmployeeHoursOverviewComponent implements OnInit, OnDestroy {
  private store = inject<Store<AppState>>(Store);
  private fb = inject(UntypedFormBuilder);
  private pageFiltersService = inject(PageFiltersService);
  private helper = inject(TimesheetHelperService);
  private timesheetDayService = inject(TimesheetDayService);
  private timesheetService = inject(TimesheetService);
  #cd = inject(ChangeDetectorRef);

  canCreateTimesheets = usePermission(() => ({
    permissions: ['Create timesheets', 'Create own timesheet'],
    userId: 'me',
  }));

  @Input()
  title: string;

  private employeeChange$: BehaviorSubject<any> = new BehaviorSubject({});
  private _employeeId = '';

  today = plainDateToday();

  periodInputId = generateId('employee-hours-period');

  periodForm = this.fb.group({
    period: new FormControl<PlainDateRange>(
      { start: addPlainDays(this.today, -7), end: this.today },
      { nonNullable: true, validators: Validators.required },
    ),
  });

  planType = PlanType;

  private subscription: Subscription = new Subscription();

  openCloseData;

  viewData: EmployeeTimesheetData;
  options;
  columns: TimesheetColumns;
  isLoading = true;

  customFields: CustomFieldsModel[] = [];

  isPaidBreaksEnabled = this.store.selectSignal(anyDepartmentHasPaidBreaks);

  isAnyTimesheetFilterActive$ = this.store.select(isAnyTimesheetFilterActive);

  @ViewChild(SidebarComponent, { static: true })
  sidebar: SidebarComponent;

  showCoc: boolean;

  @Input()
  set employeeId(employee) {
    this._employeeId = employee;
    this.employeeChange$.next(employee);
    this.pageFiltersService.setFilter('timesheet', 'employeeId', employee);
  }

  get employeeId() {
    return this._employeeId;
  }

  ngOnInit() {
    this.isLoading = true;

    this.loadData();
    this.getViewData();

    this.getOptions();

    this.subscription.add(
      this.timesheetDayService.status$
        .pipe(
          filter(({ timesheet }) => !!timesheet),
          // For some reason we need to manually `save` here and subscribe.
          // otherwise the request will get cancelled straight away.
          switchMap(({ timesheet, status }) => this.timesheetService.save({ id: timesheet.id, status })),
        )
        .subscribe(),
    );

    this.subscription.add(
      this.store
        .select(getCustomFieldsByAppliesTo(CustomFieldsAppliesTo.TIMESHEET))
        .subscribe((customFields: CustomFieldsModel[]) => {
          this.customFields = customFields;
          this.#cd.markForCheck();
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Make sure the data for the given period and user is loaded into the store
   */
  private loadData() {
    this.subscription.add(
      this.employeeChange$
        .pipe(
          combineLatest(
            this.periodForm.valueChanges.pipe(
              startWith(this.periodForm.value),
              filter(() => this.periodForm.valid),
            ),
            (employeeId, formData) => ({
              employeeId,
              minDate: fromPlainDate(formData.period.start),
              maxDate: fromPlainDate(formData.period.end),
            }),
          ),
          switchMap((params) => {
            const period = {
              minDate: toISODateString(params.minDate),
              maxDate: toISODateString(params.maxDate),
            };
            this.pageFiltersService.setFilter('timesheet', 'period', period);

            const permissions: PermissionOption = ['Create timesheets', 'Create own timesheet'];

            return this.helper
              .loadDataObs(params.minDate, params.maxDate, params.employeeId)
              .pipe(
                switchMap(() =>
                  this.store.pipe(
                    select(
                      getTimesheetDepartmentsForUsers([params.employeeId], period.minDate, period.maxDate, permissions),
                    ),
                  ),
                ),
              );
          }),
        )
        .subscribe((openCloseData) => {
          this.openCloseData = openCloseData;
          this.#cd.markForCheck();
        }),
    );
  }

  private getViewData() {
    this.subscription.add(
      this.store.pipe(lazySelect(getDataForEmployeeTimesheetPage)).subscribe((data) => {
        this.viewData = data;
        if (data && data.days) {
          this.isLoading = false;
        }
        this.#cd.markForCheck();
      }),
    );
  }

  private getOptions() {
    this.subscription.add(
      this.store
        .select(getAccountCocInSchedule)
        .pipe(
          tap((showCoc) => {
            this.showCoc = showCoc;
          }),
          switchMap(() => this.store.pipe(lazySelect(getTimesheetPageOptionsForAllDepartments))),
        )
        .subscribe((options) => {
          // Handle options
          this.options = options;
          this.columns = options.columns;
          this.#cd.markForCheck();
        }),
    );
  }

  trackByDate(index: number, dayRow: EmployeeTimesheetDays) {
    return dayRow.day;
  }

  trackByRow(index: number, row: TimesheetRow) {
    const model = isTimesheetRowTimesheet(row)
      ? row.timesheet
      : isTimesheetRowAbsentee(row)
        ? row.absentee
        : row.schedule;

    return `${row.type}:${model.id}`;
  }

  openSidebar() {
    this.sidebar.open();
  }
}
